import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { paidVoucherAPI } from '../../services/api';
import SuccessModal from '../Common/SuccessModal';
import ErrorModal from '../Common/ErrorModal';
import './PaidVoucherCreate.css';

const PaidVoucherCreate = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    voucherNumber: '',
    voucherDate: new Date().toISOString().split('T')[0],
    paidTo: '',
    amount: '',
    amountInWords: '',
    paymentMethod: '',
    chequeNumber: '',
    bank: '',
    purpose: '',
    isActive: 'true',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.voucherNumber.trim()) {
      newErrors.voucherNumber = t('paidVoucher.create.errors.voucherNumberRequired');
    }
    if (!formData.voucherDate) {
      newErrors.voucherDate = t('paidVoucher.create.errors.voucherDateRequired');
    }
    if (!formData.paidTo.trim()) {
      newErrors.paidTo = t('paidVoucher.create.errors.paidToRequired');
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = t('paidVoucher.create.errors.amountRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount),
        isActive: formData.isActive === 'true',
      };
      await paidVoucherAPI.create(submitData);
      setShowSuccessModal(true);
      setTimeout(() => {
        navigate('/paid-voucher');
      }, 1000);
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else {
        setErrorMessage(err.response?.data?.message || t('paidVoucher.create.messages.createError'));
        setShowErrorModal(true);
      }
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <button className="btn-back" onClick={() => navigate('/paid-voucher')}>
          <i className="fas fa-arrow-left"></i> {t('paidVoucher.create.backToList')}
        </button>

      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-hashtag"></i> {t('paidVoucher.create.fields.voucherNumber')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-hashtag input-icon"></i>
                <input
                  type="text"
                  className={`form-input ${errors.voucherNumber ? 'is-invalid' : ''}`}
                  name="voucherNumber"
                  value={formData.voucherNumber}
                  onChange={handleChange}
                  placeholder={t('paidVoucher.create.fields.voucherNumber')}
                  maxLength={50}
                />
              </div>
              {errors.voucherNumber && (
                <div className="error-message">{errors.voucherNumber}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-calendar"></i> {t('paidVoucher.create.fields.voucherDate')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-calendar input-icon"></i>
                <input
                  type="date"
                  className={`form-input ${errors.voucherDate ? 'is-invalid' : ''}`}
                  name="voucherDate"
                  value={formData.voucherDate}
                  onChange={handleChange}
                />
              </div>
              {errors.voucherDate && (
                <div className="error-message">{errors.voucherDate}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-user"></i> {t('paidVoucher.create.fields.paidTo')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-user input-icon"></i>
                <input
                  type="text"
                  className={`form-input ${errors.paidTo ? 'is-invalid' : ''}`}
                  name="paidTo"
                  value={formData.paidTo}
                  onChange={handleChange}
                  placeholder={t('paidVoucher.create.fields.paidTo')}
                  maxLength={255}
                />
              </div>
              {errors.paidTo && (
                <div className="error-message">{errors.paidTo}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-dollar-sign"></i> {t('paidVoucher.create.fields.amount')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-dollar-sign input-icon"></i>
                <input
                  type="number"
                  step="0.01"
                  className={`form-input ${errors.amount ? 'is-invalid' : ''}`}
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder={t('paidVoucher.create.fields.amount')}
                />
              </div>
              {errors.amount && (
                <div className="error-message">{errors.amount}</div>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-file-alt"></i> {t('paidVoucher.create.fields.amountInWords')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-file-alt input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="amountInWords"
                  value={formData.amountInWords}
                  onChange={handleChange}
                  placeholder={t('paidVoucher.create.fields.amountInWords')}
                  maxLength={500}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-credit-card"></i> {t('paidVoucher.create.fields.paymentMethod')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-credit-card input-icon"></i>
                <select
                  className="form-input"
                  name="paymentMethod"
                  value={formData.paymentMethod}
                  onChange={handleChange}
                >
                  <option value="">{t('common.select')}</option>
                  <option value="Cash">{t('paidVoucher.create.paymentMethods.cash')}</option>
                  <option value="Cheque">{t('paidVoucher.create.paymentMethods.cheque')}</option>
                </select>
            </div>
          </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-file-invoice"></i> {t('paidVoucher.create.fields.chequeNumber')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-file-invoice input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="chequeNumber"
                  value={formData.chequeNumber}
                  onChange={handleChange}
                  placeholder={t('paidVoucher.create.fields.chequeNumber')}
                  maxLength={100}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-university"></i> {t('paidVoucher.create.fields.bank')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-university input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="bank"
                  value={formData.bank}
                  onChange={handleChange}
                  placeholder={t('paidVoucher.create.fields.bank')}
                  maxLength={255}
                />
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-info-circle"></i> {t('paidVoucher.create.fields.purpose')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-info-circle input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="purpose"
                  value={formData.purpose}
                  onChange={handleChange}
                  placeholder={t('paidVoucher.create.fields.purpose')}
                  maxLength={500}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-check-circle"></i> {t('paidVoucher.create.fields.isActive')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-check-circle input-icon"></i>
                <select
                  className="form-input"
                  name="isActive"
                  value={formData.isActive}
                  onChange={handleChange}
                >
                  <option value="true">{t('common.yes')}</option>
                  <option value="false">{t('common.no')}</option>
                </select>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={() => navigate('/paid-voucher')}
            >
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? t('common.loading') : t('paidVoucher.create.save')}
            </button>
          </div>
        </form>
      </div>

      <SuccessModal
        show={showSuccessModal}
        message={t('paidVoucher.create.messages.createSuccess')}
        onClose={() => {
          setShowSuccessModal(false);
        }}
      />

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </div>
  );
};

export default PaidVoucherCreate;

